/**
 * LoggingService.ts
 * 
 * Central logging service for the Meduler application that provides
 * category-based filtering to reduce console noise during development.
 */

// Log levels in order of increasing severity
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

// Categories for different parts of the application
export type LogCategory = 
  | 'profile' 
  | 'medication' 
  | 'database' 
  | 'auth' 
  | 'ui' 
  | 'service-worker' 
  | 'general' 
  | 'cache' 
  | 'sync' 
  | 'conflict' 
  | 'network' 
  | 'performance'
  | 'optimistic-ui';

// Configuration for the logging service
interface LoggingConfig {
  minLevel: LogLevel;
  enabledCategories: Set<LogCategory>;
}

class LoggingService {
  private config: LoggingConfig;
  private readonly LOCAL_STORAGE_KEY = 'meduler_logging_config';

  constructor() {
    // Default configuration
    this.config = {
      // Always set minimum level to WARN to reduce console noise
      minLevel: LogLevel.WARN,
      enabledCategories: new Set<LogCategory>(['general'])
    };

    // Load saved configuration if available
    this.loadConfig();
  }

  /**
   * Load logging configuration from localStorage
   */
  private loadConfig(): void {
    try {
      const savedConfig = localStorage.getItem(this.LOCAL_STORAGE_KEY);
      if (savedConfig) {
        const parsed = JSON.parse(savedConfig);
        this.config = {
          minLevel: parsed.minLevel ?? this.config.minLevel,
          enabledCategories: new Set<LogCategory>(parsed.enabledCategories ?? ['general'])
        };
      }
    } catch (error) {
      
    }
  }

  /**
   * Save current configuration to localStorage
   */
  private saveConfig(): void {
    try {
      localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify({
        minLevel: this.config.minLevel,
        enabledCategories: Array.from(this.config.enabledCategories)
      }));
    } catch (error) {
      
    }
  }

  /**
   * Check if a category is enabled
   */
  isCategoryEnabled(category: LogCategory): boolean {
    return this.config.enabledCategories.has(category);
  }

  /**
   * Enable a specific log category
   */
  enableCategory(category: LogCategory): void {
    this.config.enabledCategories.add(category);
    this.saveConfig();
  }

  /**
   * Disable a specific log category
   */
  disableCategory(category: LogCategory): void {
    this.config.enabledCategories.delete(category);
    this.saveConfig();
  }

  /**
   * Enable all log categories
   */
  enableAllCategories(): void {
    [
      'profile', 'medication', 'database', 'auth', 'ui', 'service-worker', 'general',
      'cache', 'sync', 'conflict', 'network', 'performance'
    ].forEach(cat => {
      this.config.enabledCategories.add(cat as LogCategory);
    });
    this.saveConfig();
  }

  /**
   * Disable all log categories
   */
  disableAllCategories(): void {
    this.config.enabledCategories.clear();
    this.saveConfig();
  }

  /**
   * Set the minimum log level
   */
  setMinLevel(level: LogLevel): void {
    this.config.minLevel = level;
    this.saveConfig();
  }

  /**
   * Log a debug message
   */
  debug(category: LogCategory, message: string): void {
    if (this.config.minLevel <= LogLevel.DEBUG && this.config.enabledCategories.has(category)) {
       
      console.debug(`[${category}] ${message}`);
    }
  }

  /**
   * Log an info message
   */
  info(category: LogCategory, message: string): void {
    if (this.config.minLevel <= LogLevel.INFO && this.config.enabledCategories.has(category)) {
       
      console.info(`[${category}] ${message}`);
    }
  }

  /**
   * Log a warning message
   */
  warn(category: LogCategory, message: string): void {
    if (this.config.minLevel <= LogLevel.WARN && this.config.enabledCategories.has(category)) {
       
      console.warn(`[${category}] ${message}`);
    }
  }

  /**
   * Log an error message
   */
  error(category: LogCategory, message: string): void {
    if (this.config.minLevel <= LogLevel.ERROR && this.config.enabledCategories.has(category)) {
       
      console.error(`[${category}] ${message}`);
    }
  }

  /**
   * Log a performance metric
   */
  performance(category: LogCategory, operation: string, timeMs: number): void {
    if (this.config.minLevel <= LogLevel.INFO && this.config.enabledCategories.has(category)) {
       
      console.info(`[${category}] Performance: ${operation} took ${timeMs}ms`);
    }
  }
}

// Export singleton instance
export const loggingService = new LoggingService();
